import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { concatMap, first, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { InvestmentScenariiService } from './investment-scenarii.service';

import { APIService } from '../api';
import { KPI } from '../api/models';
import { InvestmentsService } from './investments.service';
import { FilterModel } from 'ag-grid-community';

@Injectable()
export class PostponementsService {

  private _postponements$: Observable<KPI.MappedPostponement[]>;
  private postponementsRefresh$ = new BehaviorSubject<void>(null);
  private _postponementReasons$: Observable<KPI.PostponementReason[]>;
  private postponementReasonsRefresh$ = new BehaviorSubject<void>(null);

  constructor(
    private api: APIService,
    private investmentsService: InvestmentsService,
    private investmentScenariiService: InvestmentScenariiService,
  ) {
  }

  public get postponementReasons$(): Observable<KPI.PostponementReason[]> {
    if (!this._postponementReasons$) {
      this._postponementReasons$ = this.postponementReasonsRefresh$.pipe(
        switchMap(() => this.api.investments_postponement_reasons(null, false)),
        shareReplay(1),
      );
    }

    return this._postponementReasons$;
  }

  public refreshPostponementReasons(): void {
    this.postponementReasonsRefresh$.next();
  }

  public get postponements$(): Observable<KPI.MappedPostponement[]> {
    if (!this._postponements$) {
      this._postponements$ = this.postponementsRefresh$.pipe(
        switchMap(() => this.api.investments_postponements(null, false).pipe(
          concatMap((postponements) => this.mapPostponements(postponements)),
        )),
        shareReplay(1),
      );
    }

    return this._postponements$;
  }

  public refreshPostponements(): void {
    this.postponementsRefresh$.next();
  }

  public getPostponementsByYears(params: FilterModel) {
    return this.api.postponements_table(params)
  }

  public createPostponement(
    postponement: Partial<KPI.Postponement>,
  ): Observable<KPI.Postponement> {
    return this.api.investments_postponements_post(postponement).pipe(
      tap(() => this.refreshPostponements()),
    );
  }

  private mapPostponements(postponements: KPI.Postponement[]): Observable<KPI.MappedPostponement[]> {
    return combineLatest(
      combineLatest(postponements.map(p => this.investmentsService.getInvestment(p.investment))),
      this.postponementReasons$,
      this.investmentScenariiService.mainScenario$,
    ).pipe(
      first(),
      map(([mappedPostponements, reasons, scenario]) => {
        return postponements.reduce((arr, postponement) => {
          const investment_object = mappedPostponements.find((inv) =>
            inv.id === postponement.investment && inv.scenario === scenario.id,
          );
          if (investment_object) { // If no invest in db, the postponement doest not exist anymore
            arr.push({
              ...postponement,
              price: Number(postponement.price),
              investment_object,
              reason_object: postponement.reason && reasons.find((reason) => reason.id === postponement.reason),
            });
          }
          return arr;
        }, []);
      }),
    );
  }

}
